import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Google Pay Button Component for v2 Architecture
 * Uses v2 useExpressPaymentMethods hook and follows clean architecture principles
 */
import GooglePayButtonReact from '@google-pay/button-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useExpressPaymentMethods } from '../hooks/useExpressPaymentMethods';
import { usePaymentQuery } from '../hooks/usePaymentQuery';
import { useShippingRatesQuery } from '../hooks/useShippingRatesQuery';
import { useStepConfig } from '../hooks/useStepConfig';
import { getBasisTheoryKeys } from '../../../config/basisTheory';
export const GooglePayButton = ({ className = '', disabled = false, onSuccess, onError, onCancel, checkout, size = 'lg', buttonColor = 'black', buttonType = 'plain', requiresShipping: requiresShippingProp, }) => {
    const { googlePayPaymentMethod, shippingMethods, lineItems, handleAddExpressId, updateCheckoutSessionValues, updateCustomerEmail, reComputeOrderSummary, setError: setContextError, } = useExpressPaymentMethods();
    // Use payment query hook for processing
    const { processGooglePayPayment } = usePaymentQuery();
    // Use shipping rates hook for selecting shipping rates
    const { selectRate } = useShippingRatesQuery({ checkout });
    // Get Basis Theory credentials based on sandboxed flag from payment method config
    // When sandboxed is true (even on production hostname), use test keys
    const { basistheoryPublicKey, basistheoryTenantId } = useMemo(() => {
        const useProductionKeys = googlePayPaymentMethod?.metadata?.sandboxed === false;
        const keys = getBasisTheoryKeys(useProductionKeys);
        console.log('[GooglePayButton] Using Basis Theory keys:', {
            sandboxed: googlePayPaymentMethod?.metadata?.sandboxed,
            useProductionKeys,
            tenantId: keys.tenantId,
        });
        return {
            basistheoryPublicKey: keys.apiKey,
            basistheoryTenantId: keys.tenantId,
        };
    }, [googlePayPaymentMethod?.metadata?.sandboxed]);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [pendingPaymentData, setPendingPaymentData] = useState(null);
    const [googlePayError, setGooglePayError] = useState(null);
    // Per-step country allow-list (CRM-injected stepConfig). Empty/missing = all allowed.
    const { stepConfig } = useStepConfig();
    const countryAllowlist = useMemo(() => {
        const list = stepConfig?.addressSettings?.countryAllowlist;
        if (!list || list.length === 0)
            return undefined;
        return list.map((c) => c.toUpperCase());
    }, [stepConfig]);
    // Don't render if no Google Pay payment method is enabled
    if (!googlePayPaymentMethod) {
        return null;
    }
    // Register express payment method
    useEffect(() => {
        handleAddExpressId('google_pay');
    }, [handleAddExpressId]);
    // Convert Google Pay address to internal Address format
    const googlePayAddressToAddress = useCallback((googlePayAddress) => {
        return {
            address1: googlePayAddress.address1 || '',
            address2: googlePayAddress.address2 || '',
            lastName: googlePayAddress.name?.split(' ').slice(-1)[0] || '',
            firstName: googlePayAddress.name?.split(' ').slice(0, -1).join(' ') || '',
            city: googlePayAddress.locality || '',
            state: googlePayAddress.administrativeArea || '',
            country: googlePayAddress.countryCode || '',
            postal: googlePayAddress.postalCode || '',
            phone: googlePayAddress.phoneNumber || '',
            email: '',
        };
    }, []);
    // Tokenize Google Pay data using Basis Theory
    const tokenizeGooglePayTokenWithBasisTheory = useCallback(async (paymentData) => {
        try {
            const googlePayTokenString = paymentData.paymentMethodData.tokenizationData.token;
            const googlePayToken = JSON.parse(googlePayTokenString);
            const response = await fetch('https://api.basistheory.com/google-pay', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': basistheoryPublicKey,
                },
                body: JSON.stringify({
                    google_payment_data: googlePayToken,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Basis Theory API error:', errorData);
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const jsonResponse = await response.json();
            return jsonResponse?.token_intent || jsonResponse?.google_pay;
        }
        catch (error) {
            console.error('Error tokenizing Google Pay data:', error);
            throw error;
        }
    }, []);
    // Process Google Pay payment using SDK hook
    const handleGooglePayPayment = useCallback(async (token) => {
        setProcessingPayment(true);
        try {
            const result = await processGooglePayPayment(checkout.checkoutSession.id, token, {
                onPaymentSuccess: (response) => {
                    // Keep processing state true during navigation
                },
                onPaymentFailed: (err) => {
                    setProcessingPayment(false);
                    setGooglePayError(err.message);
                    setContextError(err.message);
                    throw new Error(err.message);
                },
            });
            return result;
        }
        catch (error) {
            console.error('Payment processing failed:', error);
            setProcessingPayment(false);
            const errorMessage = error instanceof Error ? error.message : 'Google Pay payment failed';
            setGooglePayError(errorMessage);
            setContextError(errorMessage);
            throw error;
        }
    }, [processGooglePayPayment, checkout.checkoutSession.id, setContextError]);
    // Process payment data
    const onGooglePaymentData = useCallback(async (paymentData) => {
        setProcessingPayment(true);
        try {
            // Extract billing address if available
            let billingAddress;
            if (paymentData.paymentMethodData.info?.billingAddress) {
                billingAddress = googlePayAddressToAddress(paymentData.paymentMethodData.info.billingAddress);
            }
            // Extract shipping address if available
            let shippingAddress;
            if (paymentData.shippingAddress) {
                shippingAddress = googlePayAddressToAddress(paymentData.shippingAddress);
            }
            // Defense-in-depth: reject if wallet-returned country isn't in allowlist
            if (countryAllowlist &&
                shippingAddress?.country &&
                !countryAllowlist.includes(shippingAddress.country.toUpperCase())) {
                const msg = 'Shipping to this country is not supported';
                console.error('[GooglePay] Shipping country not in allowlist:', shippingAddress.country);
                setProcessingPayment(false);
                setGooglePayError(msg);
                setContextError(msg);
                if (onError)
                    onError(msg);
                return;
            }
            // Update checkout session with addresses before processing payment
            if (shippingAddress) {
                await updateCheckoutSessionValues({
                    data: {
                        shippingAddress,
                        billingAddress: billingAddress ?? null,
                    },
                });
            }
            // Update customer email if provided
            if (paymentData.email) {
                await updateCustomerEmail({
                    data: {
                        email: paymentData.email,
                    },
                });
            }
            const payToken = await tokenizeGooglePayTokenWithBasisTheory(paymentData);
            const result = await handleGooglePayPayment(payToken);
            // Call success callback
            if (onSuccess) {
                onSuccess(result);
            }
        }
        catch (error) {
            console.error('Error processing Google Pay payment:', error);
            const errorMessage = error instanceof Error ? error.message : 'Payment Failed';
            setProcessingPayment(false);
            setGooglePayError(errorMessage);
            setContextError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        }
    }, [
        googlePayAddressToAddress,
        updateCheckoutSessionValues,
        updateCustomerEmail,
        tokenizeGooglePayTokenWithBasisTheory,
        handleGooglePayPayment,
        countryAllowlist,
        onSuccess,
        onError,
        setContextError,
    ]);
    // Effect to process payment data in background
    useEffect(() => {
        if (pendingPaymentData && !processingPayment) {
            const processPaymentInBackground = async () => {
                try {
                    await onGooglePaymentData(pendingPaymentData);
                }
                catch (error) {
                    console.error('Background payment processing failed:', error);
                }
                finally {
                    setPendingPaymentData(null);
                }
            };
            void processPaymentInBackground();
        }
    }, [pendingPaymentData, processingPayment, onGooglePaymentData]);
    // Handle payment data changes during the flow
    const handleGooglePayDataChanged = useCallback((intermediatePaymentData) => {
        return new Promise((resolve) => {
            const processCallback = async () => {
                try {
                    const paymentDataRequestUpdate = {};
                    if (intermediatePaymentData.callbackTrigger === 'SHIPPING_ADDRESS') {
                        const address = intermediatePaymentData.shippingAddress;
                        const addressCountry = (address?.countryCode || '').toUpperCase();
                        // Defense-in-depth: reject if selected country isn't in allowlist
                        if (countryAllowlist && addressCountry && !countryAllowlist.includes(addressCountry)) {
                            resolve({
                                error: {
                                    reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
                                    message: 'Shipping to this country is not supported',
                                    intent: 'SHIPPING_ADDRESS',
                                },
                            });
                            return;
                        }
                        const shippingAddress = {
                            address1: address?.addressLines?.[0] || '',
                            address2: address?.addressLines?.[1] || '',
                            lastName: address?.name?.split(' ').slice(-1)[0] || '',
                            firstName: address?.name?.split(' ').slice(0, -1).join(' ') || '',
                            city: address?.locality || '',
                            state: address?.administrativeArea || '',
                            country: address?.countryCode || '',
                            postal: address?.postalCode || '',
                            phone: address?.phoneNumber || '',
                            email: '',
                        };
                        await updateCheckoutSessionValues({
                            data: { shippingAddress },
                        });
                        const newOrderSummary = await reComputeOrderSummary();
                        if (newOrderSummary) {
                            // Use selected shipping rate ID if available, otherwise fall back to first one
                            const defaultSelectedId = newOrderSummary.selectedShippingRateId ||
                                newOrderSummary.shippingMethods[0]?.identifier ||
                                '';
                            paymentDataRequestUpdate.newShippingOptionParameters = {
                                defaultSelectedOptionId: defaultSelectedId,
                                shippingOptions: newOrderSummary.shippingMethods.map((method) => ({
                                    id: method.identifier,
                                    label: method.label,
                                    description: method.amount + ': ' + method.detail || '',
                                })),
                            };
                            paymentDataRequestUpdate.newTransactionInfo = {
                                totalPriceStatus: 'FINAL',
                                totalPrice: newOrderSummary.total.amount,
                                currencyCode: checkout.summary?.currency || 'USD',
                            };
                        }
                    }
                    else if (intermediatePaymentData.callbackTrigger === 'SHIPPING_OPTION') {
                        // Update shipping rate
                        if (intermediatePaymentData.shippingOptionData?.id) {
                            try {
                                await selectRate(intermediatePaymentData.shippingOptionData.id);
                                const newOrderSummary = await reComputeOrderSummary();
                                if (newOrderSummary) {
                                    paymentDataRequestUpdate.newTransactionInfo = {
                                        totalPriceStatus: 'FINAL',
                                        totalPrice: newOrderSummary.total.amount,
                                        currencyCode: checkout.summary?.currency || 'USD',
                                    };
                                }
                            }
                            catch (error) {
                                console.error('Error selecting shipping rate:', error);
                                throw error;
                            }
                        }
                    }
                    else if (intermediatePaymentData.callbackTrigger === 'OFFER') {
                        // No action needed for OFFER callback
                    }
                    else if (intermediatePaymentData.callbackTrigger === 'INITIALIZE') {
                        // No action needed for INITIALIZE callback
                    }
                    resolve(paymentDataRequestUpdate);
                }
                catch (error) {
                    console.error('Error in onPaymentDataChanged:', error);
                    resolve({
                        error: {
                            reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
                            message: 'Unable to calculate shipping for this address',
                            intent: intermediatePaymentData.callbackTrigger,
                        },
                    });
                }
            };
            void processCallback();
        });
    }, [updateCheckoutSessionValues, reComputeOrderSummary, checkout, selectRate, countryAllowlist]);
    // Handle payment authorization
    const handleGooglePayAuthorized = useCallback((paymentData) => {
        setPendingPaymentData(paymentData);
        return Promise.resolve({ transactionState: 'SUCCESS' });
    }, []);
    // Don't render if no order summary
    if (!checkout.summary) {
        return null;
    }
    const minorUnitsToCurrencyString = (amountMinor, currency) => {
        return (amountMinor / 100).toFixed(2);
    };
    const allowedCardNetworks = ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'];
    const allowedCardAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];
    const baseCardPaymentMethod = {
        type: 'CARD',
        parameters: {
            allowedAuthMethods: allowedCardAuthMethods,
            allowedCardNetworks: allowedCardNetworks,
            billingAddressRequired: true,
            billingAddressParameters: {
                format: 'FULL',
                phoneNumberRequired: true,
            },
        },
    };
    const tokenizationSpecification = {
        type: 'PAYMENT_GATEWAY',
        parameters: {
            gateway: 'basistheory',
            gatewayMerchantId: basistheoryTenantId,
        },
    };
    // Determine if shipping is required: use prop if provided, otherwise auto-detect from shippingMethods
    const requiresShipping = requiresShippingProp ?? shippingMethods.length > 0;
    const paymentRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
            {
                ...baseCardPaymentMethod,
                tokenizationSpecification,
            },
        ],
        transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: minorUnitsToCurrencyString(checkout.summary.totalAdjustedAmount, checkout.summary.currency),
            currencyCode: checkout.summary.currency,
        },
        merchantInfo: {
            merchantName: googlePayPaymentMethod?.metadata?.merchantName ||
                checkout.checkoutSession?.store?.name ||
                'Store',
            // Use test merchant ID for sandbox, actual merchant ID for production
            merchantId: googlePayPaymentMethod?.metadata?.sandboxed
                ? '12345678901234567890' // Google's test merchant ID for sandbox
                : googlePayPaymentMethod?.metadata?.merchantId || '12345678901234567890',
        },
        shippingAddressRequired: requiresShipping,
        shippingOptionRequired: requiresShipping,
        emailRequired: true,
        callbackIntents: requiresShipping
            ? ['SHIPPING_OPTION', 'SHIPPING_ADDRESS', 'PAYMENT_AUTHORIZATION']
            : ['PAYMENT_AUTHORIZATION'],
        ...(requiresShipping && {
            shippingAddressParameters: countryAllowlist
                ? { allowedCountryCodes: countryAllowlist }
                : undefined,
            shippingOptionParameters: {
                defaultSelectedOptionId: checkout.checkoutSession?.shippingRate?.id ||
                    (shippingMethods.length > 0 ? shippingMethods[0].identifier : ''),
                shippingOptions: shippingMethods.map((method) => ({
                    id: method.identifier,
                    label: method.label,
                    description: method.amount + ': ' + method.detail || '',
                })),
            },
        }),
    };
    const environment = googlePayPaymentMethod?.metadata?.sandboxed ? 'TEST' : 'PRODUCTION';
    // Button size classes
    const sizeClasses = {
        sm: 'h-8',
        md: 'h-10',
        lg: 'h-12',
    };
    const Button = ({ children }) => (_jsx("button", { type: "button", disabled: true, className: `w-full rounded-md bg-black text-base text-white shadow-sm hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-50 ${sizeClasses[size]} `, children: _jsxs("div", { className: "flex items-center justify-center gap-3", children: [_jsx("div", { className: "h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" }), children] }) }));
    return (_jsxs("div", { className: "w-full", children: [!processingPayment ? (_jsx(GooglePayButtonReact, { environment: environment, paymentRequest: paymentRequest, onPaymentAuthorized: handleGooglePayAuthorized, onPaymentDataChanged: handleGooglePayDataChanged, onError: (error) => {
                    console.error('Google Pay error:', error);
                    const errorMessage = 'Google Pay error: ' + error.statusMessage;
                    setGooglePayError(errorMessage);
                    setContextError(errorMessage);
                    if (onError) {
                        onError(errorMessage);
                    }
                }, onCancel: () => {
                    if (onCancel) {
                        onCancel();
                    }
                }, existingPaymentMethodRequired: false, buttonColor: buttonColor, buttonType: buttonType, buttonSizeMode: "fill", buttonLocale: "en", className: `m-0 w-full rounded-sm p-0 text-base text-white ${sizeClasses[size]} ${className}` })) : (_jsx(Button, { children: _jsx("span", { className: "font-medium", children: "Processing..." }) })), googlePayError && (_jsx("div", { className: "mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600", children: googlePayError }))] }));
};
export default GooglePayButton;
